/**
 * The whole assistant surface. One component, rendered by the right panel
 * tab, the project dialog and the mobile sheet. Do not fork it per surface.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Archive, Check, ChevronDown, Plus, Square } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { LOCAL_WORKSPACE_ID } from "@/data/workspace";
import { getData, useDataContextId, useItem, useSpaceOf } from "@/data/store";
import { useFolderMeta } from "@/data/sharedFolders";
import { useAuth } from "@/data/auth";
import { useTabs } from "@/store/tabs";
import { useUi } from "@/store/ui";
import { useChat } from "@/store/chat";
import { scopeKey, type ChatScope } from "@/data/chat";
import { MAX_CHAT_MESSAGE_CHARS } from "@hydra-notes/shared";
import { toast } from "@/ui/toast";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { useSignInDialog } from "@/components/SignInDialog";
import { Transcript } from "./Transcript";
import s from "./chat.module.css";

export function ChatPane({ forceScope }: { forceScope?: ChatScope }) {
  const chatScope = useUi((u) => u.chatScope);
  const activeItemId = useTabs((t) => {
    const g = t.groups.find((g) => g.id === t.activeGroupId);
    return g?.tabs.find((tab) => tab.id === g.activeTabId)?.itemId ?? null;
  });
  const item = useItem(activeItemId);
  const itemSpace = useSpaceOf(item?.id ?? null);
  const folderMeta = useFolderMeta(itemSpace?.kind === "shared" ? itemSpace.spaceId : null);
  const scope: ChatScope =
    forceScope ??
    (chatScope === "note" && item?.type === "note"
      ? {
          kind: "note",
          noteId: item.id,
          // shared note: the thread lives in the folder's space (role-gated
          // server-side); workspace-scope chat always stays personal
          ...(itemSpace?.kind === "shared" ? { spaceId: itemSpace.spaceId } : {}),
        }
      : { kind: "workspace", noteId: null });
  const key = scopeKey(scope);
  const isViewerScope = !!scope.spaceId && folderMeta?.role === "viewer";

  const status = useAuth((a) => a.status);
  useDataContextId();
  const isLocal = (() => {
    try {
      return getData().workspaceId === LOCAL_WORKSPACE_ID;
    } catch {
      return true;
    }
  })();

  const chat = useChat();
  const threadId = chat.activeThread[key] ?? null;
  const messages = threadId ? (chat.messages[threadId] ?? []) : [];
  const run = threadId ? chat.runs[threadId] : undefined;
  const threads = chat.threads[key] ?? [];
  const signedIn = status === "signedIn" && !isLocal;

  useEffect(() => {
    if (!signedIn) return;
    void chat.loadModels();
    void chat.loadThreads(scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, key]);

  if (!signedIn) {
    return (
      <div className={s.empty}>
        <p>Claude works with the Notes, Tasks and Cards in your account, so it needs you signed in.</p>
        <Button variant="solid" size="sm" onClick={() => useSignInDialog.getState().setOpen(true)}>
          Sign in
        </Button>
      </div>
    );
  }

  return (
    <div className={s.pane}>
      <ThreadBar scope={scope} threads={threads} activeId={threadId} />
      {messages.length === 0 && !run ? (
        <div className={s.empty}>
          <p>
            {scope.kind === "note"
              ? isViewerScope
                ? `Ask about "${item?.title || "this note"}". You have viewer access here, so Claude can read but not edit.`
                : `Ask about "${item?.title || "this note"}". Claude can read it, and edit it if you ask.`
              : "Ask about anything in Notes, Tasks or Cards. Claude will search the right product and can make changes when you ask."}
          </p>
        </div>
      ) : (
        <Transcript messages={messages} run={run} />
      )}
      {run ? <RunStatus threadId={run.threadId} startedAt={run.startedAt} /> : null}
      <Composer scope={scope} running={!!run} />
    </div>
  );
}

function ThreadBar({
  scope,
  threads,
  activeId,
}: {
  scope: ChatScope;
  threads: { id: string; title: string }[];
  activeId: string | null;
}) {
  const chat = useChat();
  const models = chat.models;
  const active = threads.find((t) => t.id === activeId);
  return (
    <div className={s.threadBar}>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button type="button" className={s.threadPick}>
            <span className={s.threadName}>{active?.title || "New conversation"}</span>
            <ChevronDown size={11} />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="hn-menu" align="start" sideOffset={4}>
            {threads.length === 0 ? <div className="hn-menu-label">No conversations yet</div> : null}
            {threads.map((t) => (
              <DropdownMenu.Item
                key={t.id}
                className={["hn-menu-item", s.threadItem].join(" ")}
                onSelect={() => chat.selectThread(scope, t.id)}
              >
                {t.title || "Untitled"}
              </DropdownMenu.Item>
            ))}
            {activeId ? (
              <>
                <div className="hn-menu-sep" />
                <DropdownMenu.Item className="hn-menu-item" onSelect={() => void chat.archiveThread(scope, activeId)}>
                  <Archive size={12} /> Archive this conversation
                </DropdownMenu.Item>
              </>
            ) : null}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {models && models.list.length > 1 ? (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" className={s.modelPick}>
              {models.list.find((m) => m.id === models.current)?.label ?? "Model"}
              <ChevronDown size={10} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="hn-menu" align="end" sideOffset={4}>
              {models.list.map((m) => (
                <DropdownMenu.Item key={m.id} className={s.modelItem} onSelect={() => chat.setModel(m.id)}>
                  <span className={s.modelRow}>
                    {m.label}
                    {m.id === models.current ? <Check size={12} className={s.modelCheck} /> : null}
                  </span>
                  <span className={s.modelBlurb}>{m.blurb}</span>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ) : null}

      <IconButton label="New conversation" onClick={() => void chat.newThread(scope)}>
        <Plus size={14} />
      </IconButton>
    </div>
  );
}

function RunStatus({ threadId, startedAt }: { threadId: string; startedAt: number }) {
  const stop = useChat((c) => c.stop);
  const run = useChat((c) => c.runs[threadId]);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const seconds = Math.floor((Date.now() - startedAt) / 1000);
  const busy = run?.tools.find((t) => t.status === "running");
  return (
    <div className={s.runStatus}>
      <span className={s.runDot} />
      <span className={s.runVerb}>{busy ? busy.name.replace(/^mcp__notes__/, "").replace(/_/g, " ") : "Thinking"}</span>
      <span className={s.runTime}>
        {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
      </span>
      <Button variant="subtle" size="sm" onClick={() => stop(threadId)}>
        <Square size={11} /> Stop
      </Button>
    </div>
  );
}

function Composer({ scope, running }: { scope: ChatScope; running: boolean }) {
  const send = useChat((c) => c.send);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  /**
   * Grow with the draft instead of staying two rows tall.
   *
   * Measured from scrollHeight, which needs the height reset first or the
   * box can only ever grow. CSS caps it with max-height and turns on its own
   * scrollbar past that, so a very long prompt scrolls in a usefully sized
   * window rather than a two-line peephole.
   */
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // a hidden or not-yet-laid-out textarea measures 0; writing that back
    // would collapse the box, so leave the CSS height in charge instead
    if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(resize, [text, resize]);

  const tooLong = text.length > MAX_CHAT_MESSAGE_CHARS;
  // only worth showing as the limit comes into view
  const showCount = text.length > MAX_CHAT_MESSAGE_CHARS * 0.9;

  const submit = async () => {
    const value = text.trim();
    if (!value || running || sending) return;
    if (tooLong) {
      toast.error(
        `That message is ${text.length.toLocaleString()} characters, over the ${MAX_CHAT_MESSAGE_CHARS.toLocaleString()} limit.`,
      );
      return;
    }
    // cleared for responsiveness, restored below if the run never started, so
    // a rejected prompt is never silently destroyed
    setText("");
    setSending(true);
    let ok = false;
    try {
      ok = await send(scope, value);
    } finally {
      setSending(false);
    }
    if (!ok) {
      // only if the box is still empty: the user may have started retyping
      setText((current) => (current ? current : value));
      ref.current?.focus();
    }
  };

  return (
    <div className={s.composer}>
      <div className={s.inputWrap}>
        <textarea
          ref={ref}
          className={[s.input, "u-selectable"].join(" ")}
          value={text}
          rows={2}
          placeholder={running ? "Claude is working" : "Ask about your notes"}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        {showCount ? (
          <span className={[s.charCount, tooLong ? s.charCountOver : ""].join(" ")}>
            {text.length.toLocaleString()} / {MAX_CHAT_MESSAGE_CHARS.toLocaleString()}
          </span>
        ) : null}
      </div>
      <Button variant="solid" size="sm" onClick={() => void submit()} disabled={running || sending || !text.trim() || tooLong}>
        Send
      </Button>
    </div>
  );
}
